import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import {
  ArrowUp,
  CreditCard,
  Heading,
  Image as ImageIcon,
  Link2,
  MousePointerClick,
  Square,
  TextCursorInput,
  Type,
  X,
  type LucideIcon,
} from "lucide-react";
import { useStudioStore } from "../../../store/studio-store";
import {
  canCommitComposerDraft,
  isComposerCommitKey,
} from "@/lib/composer-commit";
import { useChannelDispatch } from "@/features/channel-router";
import type { RefinementContext } from "@/features/_shared/channel-types";
import {
  appendAgentWorkspaceRequirementToProject,
  createAgentWorkspaceRequirementDraft,
  resolveAgentWorkspaceAnchorFromChatContext,
} from "@/features/agent-workspace";
import {
  appendCogoCollabTransactionToProject,
  createCogoCollabTransactionFromRequirement,
  readCogoCollabLedger,
} from '@/features/collaboration-kernel'
import { emitIntentPreview } from '@/features/agent/hooks/use-intent-preview'
import { buildIntentPreviewFromAgentWorkspaceRequirement } from '@/features/agent/services/agent-workspace-semantic-patch'
import { submitIntentChunkPlan } from '@/features/chat/intent-chunks'
import { routePastedIntakeDocument } from '@/features/chat/services/route-pasted-intake-document'
import { requestPreviewProposalBody } from '@/features/agent/services/preview-proposal-chat-service'
import { useStudioLocale } from '@/i18n'
import type { LocaleCode } from '@/pages/cogo-marketing/i18n'
import { COOGO_IDE_CHROME_COPY } from '../coogo-ide-chrome-copy'
import {
  DOCUMENT_NOT_GENERATION_SOURCE_REASON,
  LOCAL_EDIT_PLAN_UNAVAILABLE_REASON,
} from './miss-reason-copy'
import {
  readOpenDocumentSource,
  useOpenDocumentSource,
} from '@/features/chat/services/open-document-source-signal'

const CHAT_RAIL_COPY: Record<
  LocaleCode,
  {
    clearTarget: string
    changeThis: string
    editPrompt: string
    proposalFirst: string
    capabilitySummary: string
    selectTarget: string
    confirmationRequired: string
    /**
     * Composer footer shown WHILE a document source is open. An imported
     * document is readable and searchable in Studio but is not an input to
     * generation (measured 2026-07-30 — see
     * `features/chat/services/open-document-source-signal.ts`). Saying so here,
     * before anything is typed, is the point: the previous behaviour let a
     * creator describe a screen "based on" the document, built something
     * unrelated, and never mentioned the document at all.
     */
    documentNotUsedForBuild: string
    targetLinked: string
    reviewBeforeApply: string
  }
> = {
  en: {
    clearTarget: 'Clear target',
    changeThis: 'Change this...',
    editPrompt: 'Describe what you want to change...',
    proposalFirst: 'Proposal first',
    capabilitySummary: 'Chat can edit pages, data, behavior, and publish.',
    selectTarget: 'Select a target element before applying this chat edit.',
    confirmationRequired: 'This WebMCP operation is available, but needs confirmation before execution.',
    documentNotUsedForBuild:
      'Your open document can be read and searched, but it is not used to build an app yet.',
    targetLinked: 'Target linked.',
    reviewBeforeApply: 'Proposed change must be reviewed before applying.',
  },
  ko: {
    clearTarget: '대상 지우기',
    changeThis: '이 항목을 변경하세요...',
    editPrompt: '변경할 내용을 입력하세요...',
    proposalFirst: '먼저 제안합니다',
    capabilitySummary: '채팅에서 페이지, 데이터, 동작, 게시를 변경할 수 있습니다.',
    selectTarget: '이 채팅 변경을 적용하기 전에 대상 요소를 선택하세요.',
    confirmationRequired: '이 WebMCP 작업은 실행 전에 확인이 필요합니다.',
    documentNotUsedForBuild:
      '열어 둔 문서는 읽고 찾아볼 수 있지만, 아직 앱을 만드는 데는 사용되지 않습니다.',
    targetLinked: '대상이 연결되었습니다.',
    reviewBeforeApply: '제안된 변경은 적용 전에 검토해야 합니다.',
  },
  ja: {
    clearTarget: '対象をクリア',
    changeThis: 'この項目を変更...',
    editPrompt: '変更したい内容を入力...',
    proposalFirst: 'まず提案します',
    capabilitySummary: 'チャットでページ、データ、動作、公開を変更できます。',
    selectTarget: 'この変更を適用する前に対象要素を選択してください。',
    confirmationRequired: 'このWebMCP操作は実行前に確認が必要です。',
    documentNotUsedForBuild:
      '開いているドキュメントは読んだり検索したりできますが、まだアプリの生成には使われません。',
    targetLinked: '対象がリンクされました。',
    reviewBeforeApply: '提案された変更は適用前に確認が必要です。',
  },
  zh: {
    clearTarget: '清除目标',
    changeThis: '更改此项...',
    editPrompt: '描述你想要修改的内容...',
    proposalFirst: '先提出方案',
    capabilitySummary: '可在聊天中修改页面、数据、动作并发布。',
    selectTarget: '应用此聊天修改前，请先选择目标元素。',
    confirmationRequired: '此 WebMCP 操作在执行前需要确认。',
    documentNotUsedForBuild: '打开的文档可以阅读和搜索，但暂时不会用于生成应用。',
    targetLinked: '目标已关联。',
    reviewBeforeApply: '拟议更改必须在应用前审阅。',
  },
  'zh-TW': {
    clearTarget: '清除目標',
    changeThis: '更改此項...',
    editPrompt: '描述你想要修改的內容...',
    proposalFirst: '先提出方案',
    capabilitySummary: '可在聊天中修改頁面、資料、動作並發佈。',
    selectTarget: '套用此聊天修改前，請先選擇目標元素。',
    confirmationRequired: '此 WebMCP 操作在執行前需要確認。',
    documentNotUsedForBuild: '開啟的文件可以閱讀和搜尋，但暫時不會用於產生應用。',
    targetLinked: '目標已關聯。',
    reviewBeforeApply: '擬議變更必須在套用前審閱。',
  },
}

// P2-sub — element_type → Lucide icon (mirrors layer-tree.tsx:529)
const ELEMENT_ICON_MAP: Record<string, LucideIcon> = {
  button: MousePointerClick,
  input: TextCursorInput,
  text: Type,
  heading: Heading,
  image: ImageIcon,
  card: CreditCard,
  container: Square,
  link: Link2,
};

function getElementIcon(type: string): LucideIcon {
  return ELEMENT_ICON_MAP[type] ?? Square;
}

/**
 * Concept2 composer — textarea + INK_50 wrapper + Lucide ArrowUp send +
 * footer affordance.
 *
 * Source: `docs/studio/design-concept2/src/chat.jsx:136-164`. Differs from
 * concept2 only in the chip slot (cogo-studio-specific A3.4 refinement
 * context — concept2 does not model element-driven refinement).
 *
 * Enter sends, Shift+Enter inserts newline (textarea pattern). aria-label
 * on the send button is "Send" so screen readers announce a real action
 * rather than the previous "↑" glyph.
 */
export function ChatRailInputBar() {
  const { locale } = useStudioLocale()
  const copy = CHAT_RAIL_COPY[locale]
  const chromeCopy = COOGO_IDE_CHROME_COPY[locale]
  const [input, setInput] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const chip = useStudioStore((s) => s.chatRailChip)
  const setChip = useStudioStore((s) => s.setChatRailChip)
  const { dispatch } = useChannelDispatch()
  // Present from the moment a document parses — i.e. BEFORE the creator
  // describes anything — so "this source can't build an app yet" is learned
  // up front rather than after a build has already run without it.
  const openDocument = useOpenDocumentSource()

  useEffect(() => {
    const focusInput = (event: Event) => {
      const detail = (event as CustomEvent<{ draft?: string }>).detail;
      if (typeof detail?.draft === 'string') setInput(detail.draft);
      inputRef.current?.focus();
    };
    window.addEventListener("cogo:focus-chat-input", focusInput);
    return () =>
      window.removeEventListener("cogo:focus-chat-input", focusInput);
  }, []);

  const handleSubmit = async () => {
    const trimmed = input.trim();
    if (!trimmed || isSubmitting) return;
    const state = useStudioStore.getState();
    const currentProject = state.currentProject;
    // W21 rung ② — an intake DOCUMENT pasted into the change-request lane
    // (a contract, a 전문 필드 표, a legacy screen…) must not be dispatched
    // as a change prompt: the read-back cards that gate its generation live
    // in the home composer. The whole decision (classify → carry → toast →
    // navigate) now lives in `routePastedIntakeDocument` because THIS was the
    // only composer that had it — StudioComposer, the project thread's other
    // composer, sent the same paste to the element-edit lane instead (census
    // 2026-08-18 row #10). A shared definition is what keeps the two answers
    // from diverging again. Returning false means "ordinary prompt, or the
    // carry refused it" — fall through rather than swallow the input.
    if (
      routePastedIntakeDocument({
        text: trimmed,
        locale,
        hasActiveElementTarget: Boolean(chip),
      })
    ) {
      setInput('');
      return;
    }
    const chunkRefinementContext: RefinementContext | undefined = chip
      ? {
          element_id: chip.element_id,
          element_type: chip.element_type,
          current_state: {},
          block_id: chip.block_id,
        }
      : undefined;
    setIsSubmitting(true);
    try {
      const chunkSubmit = await submitIntentChunkPlan(trimmed, {
        refinementContext: chunkRefinementContext,
      });
      if (chunkSubmit.handled) {
        state.appendChatMessage({
          kind: "user",
          id: crypto.randomUUID(),
          text: trimmed,
          timestamp: Date.now(),
        });
        state.appendChatMessage({
          kind: "mutation-applied",
          id: crypto.randomUUID(),
          timestamp: Date.now(),
          tool: "modify_action",
          insertedCount: 0,
          subjects:
            chunkSubmit.changedPaths ??
            chunkSubmit.plan?.actions.flatMap((action) => action.expectedChangedPaths) ??
            [],
          auditSource: "webmcp",
        });
        setInput("");
        setChip(null);
        return;
      }
      // A request the local edit planner cannot map, sent while a document
      // source is open, is the case that used to fail dishonestly: the build
      // planner ran and produced an app with no relationship to the document
      // the user had just cited, and nothing on screen said so. Surface it in
      // EVERY prompt language (the old `locale !== "en"` carve-out let English
      // requests through to that silent path), and label it for what it is.
      const openDocument =
        chunkSubmit.blocker === "knowledge-gap" && !chip
          ? readOpenDocumentSource()
          : null;
      const shouldShowChunkBlocker =
        chunkSubmit.blocker === "requires-user-confirmation" ||
        (chunkSubmit.blocker === "ambiguous-target" && !chip) ||
        (chunkSubmit.blocker === "knowledge-gap" &&
          !chip &&
          (Boolean(openDocument) || chunkSubmit.analysis.locale !== "en"));
      if (shouldShowChunkBlocker) {
        const blocker = chunkSubmit.blocker ?? "unknown";
        state.appendChatMessage({
          kind: "user",
          id: crypto.randomUUID(),
          text: trimmed,
          timestamp: Date.now(),
        });
        if (
          blocker === "requires-user-confirmation" &&
          chunkSubmit.confirmationId &&
          chunkSubmit.confirmationExpiresAt
        ) {
          state.appendChatMessage({
            kind: "capability-confirmation",
            id: crypto.randomUUID(),
            timestamp: Date.now(),
            confirmationId: chunkSubmit.confirmationId,
            title: chunkSubmit.analysis.summary,
            tools: chunkSubmit.executedTools ?? [],
            changedPaths: chunkSubmit.changedPaths ?? [],
            expiresAt: chunkSubmit.confirmationExpiresAt,
            impact: chunkSubmit.confirmationImpact,
          });
          setInput("");
          return;
        }
        // `reason` is what the bubble humanizer keys on, so the two
        // planner-gap cases carry their OWN reason codes instead of the raw
        // `knowledge-gap`, which used to be rendered verbatim as the title.
        // `details` is deliberately omitted for them: their creator-facing
        // wording lives in `humanizeChatRequestBlocker`, and a producer detail
        // would only re-introduce a second, unlocalized string.
        const failureReason =
          blocker === 'knowledge-gap'
            ? openDocument
              ? DOCUMENT_NOT_GENERATION_SOURCE_REASON
              : LOCAL_EDIT_PLAN_UNAVAILABLE_REASON
            : blocker;
        state.appendChatMessage({
          kind: "ai-result-failure",
          id: crypto.randomUUID(),
          timestamp: Date.now(),
          reason: failureReason,
          ...(blocker === 'ambiguous-target'
            ? { details: copy.selectTarget }
            : blocker === 'requires-user-confirmation'
              ? { details: copy.confirmationRequired }
              : {}),
          intent: trimmed,
          source: "channel",
        });
        setInput("");
        return;
      }
    } finally {
      setIsSubmitting(false);
    }
    if (chip && currentProject) {
      setIsSubmitting(true);
      try {
        const createdAt = new Date().toISOString();
        const requirementId = `req-${Date.now().toString(36)}`;
        const chatContext = {
          ...state.chatContext,
          selectionSurface: state.chatContext.selectionSurface ?? "preview",
          selectedElementId:
            state.chatContext.selectedElementId ?? chip.element_id,
          selectedElementLabel:
            state.chatContext.selectedElementLabel ?? chip.label,
          selectedElementType:
            state.chatContext.selectedElementType ?? chip.element_type,
          selectedBlockName:
            state.chatContext.selectedBlockName ?? chip.block_id,
        };
        const anchor = resolveAgentWorkspaceAnchorFromChatContext(chatContext);
        const proposal = await requestPreviewProposalBody({
          userText: trimmed,
          selectedLabel: chip.label,
          selectedElementType: chip.element_type,
          chatContext,
          currentProject,
        });
        const requirement = createAgentWorkspaceRequirementDraft({
          requirementId,
          anchorId: anchor.anchorId,
          intent: "requirement",
          body: trimmed,
          createdAt,
          acceptanceCriteria: [copy.targetLinked, copy.reviewBeforeApply],
          proposal: {
            body: proposal.body,
            source: proposal.source,
            mode: proposal.mode,
          },
        });
        const requirementProject = appendAgentWorkspaceRequirementToProject(
          currentProject,
          requirement,
        );
        const transaction = createCogoCollabTransactionFromRequirement({
          transactionId: `${requirement.requirementId}.proposal`,
          versionBefore: readCogoCollabLedger(requirementProject).localVersion,
          anchor,
          requirement,
          appId: state.currentProjectId ?? undefined,
          createdAt,
        });
        const nextProject = appendCogoCollabTransactionToProject({
          project: requirementProject,
          transaction,
          userEvent: "preview.requirement.proposal",
          updatedAt: createdAt,
        });
        useStudioStore.setState({
          chatContext,
          currentProject: nextProject as never,
          isDirty: true,
        } as never);
        emitIntentPreview(
          buildIntentPreviewFromAgentWorkspaceRequirement({
            anchor,
            requirement,
          }),
        );
        setInput("");
        setChip(null);
      } finally {
        setIsSubmitting(false);
      }
      return;
    }
    // Fall-through: the local-edit planner above declined this prompt, so it is
    // a build request. `chunkRefinementContext` was for that planner (the only
    // reader of one) — the router generates a whole project and has nowhere to
    // put it, so the chip survives here only as the prefixed label in `userText`.
    const userText = chip ? `${chip.label} ${trimmed}` : trimmed;
    dispatch(userText);
    setInput("");
    setChip(null);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // IME composition-confirm Enter (Korean/CJK input) must not submit —
    // it would dispatch a half-composed prompt and abort the in-flight run.
    // The rule is `@/lib/composer-commit` now, because Bot Mode's composer was
    // missing it entirely and three private copies is how that happened.
    if (!isComposerCommitKey(e)) return;
    e.preventDefault();
    void handleSubmit();
  };

  const canSend = canCommitComposerDraft(input, isSubmitting);

  return (
    <div
      data-testid="chat-rail-input-bar"
      className="flex flex-shrink-0 flex-col border-t border-border bg-background p-3"
    >
      {chip && (
        <div
          data-testid="chat-rail-chip"
          className="mb-2 flex min-h-9 w-full min-w-0 items-center gap-2 rounded-md border border-border bg-muted/40 py-1 pl-2 pr-1 text-xs text-foreground"
          aria-live="polite"
        >
          {(() => {
            const Icon = getElementIcon(chip.element_type);
            return (
              <span
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded bg-background text-muted-foreground"
              >
                <Icon size={14} aria-hidden />
              </span>
            );
          })()}
          <span className="min-w-0 flex-1 truncate text-xs font-semibold leading-4">
            {chip.label}
          </span>
          <button
            type="button"
            aria-label={copy.clearTarget}
            onClick={() => setChip(null)}
            className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-background hover:text-foreground"
          >
            <X size={10} aria-hidden />
          </button>
        </div>
      )}
      <div
        className="flex items-end gap-2 rounded-md border border-border bg-muted/30 py-2 pl-3 pr-2 transition-colors focus-within:border-ring focus-within:ring-1 focus-within:ring-ring/30"
      >
        <textarea
          ref={inputRef}
          aria-label={chromeCopy.messageCoogo}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={chip ? copy.changeThis : copy.editPrompt}
          rows={1}
          className="max-h-24 flex-1 resize-none border-0 bg-transparent py-1.5 text-sm leading-5 text-foreground outline-none placeholder:text-muted-foreground"
        />
        <button
          type="button"
          onClick={() => void handleSubmit()}
          disabled={!canSend}
          aria-label={chromeCopy.emptyState.send}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-foreground bg-foreground text-background transition-colors hover:bg-foreground/90 disabled:cursor-default disabled:border-border disabled:bg-muted disabled:text-muted-foreground"
        >
          <ArrowUp size={13} aria-hidden strokeWidth={2.2} />
        </button>
      </div>
      <div
        data-testid="chat-rail-footer-note"
        className={`mt-2 flex items-start pl-0.5 text-[11px] leading-4 ${
          openDocument && !chip ? 'text-amber-700 dark:text-amber-400' : 'text-muted-foreground'
        }`}
      >
        {chip
          ? copy.proposalFirst
          : openDocument
            ? copy.documentNotUsedForBuild
            : copy.capabilitySummary}
      </div>
    </div>
  );
}
